import React, { useState } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Calculator, CheckCircle } from "lucide-react";
import { toast } from "../hooks/use-toast";

const RequestQuote = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postalCode: "",
    flooringType: "",
    squareFootage: "",
    projectType: "",
    timeline: "",
    additionalDetails: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: "Quote Request Submitted!",
          description: "We'll get back to you within 24 hours with a detailed quote."
        });
        
        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          address: "",
          city: "",
          postalCode: "",
          flooringType: "",
          squareFootage: "",
          projectType: "",
          timeline: "",
          additionalDetails: ""
        });
      } else {
        throw new Error("Failed to submit quote request");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit quote request. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Calculator className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-5xl font-bold text-gray-800 mb-4">
            Request a Quote
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Get a personalized quote for your flooring project. Fill out the form below and we'll get back to you within 24 hours with a detailed estimate.
          </p>
        </div>

        {/* Benefits Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-lg p-6 text-center shadow-md">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-800 mb-2">Free Consultation</h3>
            <p className="text-gray-600 text-sm">No obligation quote with expert advice</p>
          </div>
          <div className="bg-white rounded-lg p-6 text-center shadow-md">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-800 mb-2">Fast Response</h3>
            <p className="text-gray-600 text-sm">Get your quote within 24 hours</p>
          </div>
          <div className="bg-white rounded-lg p-6 text-center shadow-md">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-800 mb-2">Competitive Pricing</h3>
            <p className="text-gray-600 text-sm">Best value for premium quality flooring</p>
          </div>
        </div>

        {/* Quote Form */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          <form onSubmit={handleSubmit}>
            {/* Personal Information */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Personal Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-gray-700 font-medium mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-gray-700 font-medium mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Project Location */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Project Location
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="address" className="block text-gray-700 font-medium mb-2">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="city" className="block text-gray-700 font-medium mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="postalCode" className="block text-gray-700 font-medium mb-2">
                    Postal Code *
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Project Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="flooringType" className="block text-gray-700 font-medium mb-2">
                    Flooring Type *
                  </label>
                  <select
                    id="flooringType"
                    name="flooringType"
                    value={formData.flooringType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="">Select flooring type</option>
                    <option value="ComfortSense - Water-Resistant Laminate">ComfortSense - Water-Resistant Laminate</option>
                    <option value="NatureSense - Engineered Hardwood">NatureSense - Engineered Hardwood</option>
                    <option value="Guard - Waterproof Laminate">Guard - Waterproof Laminate</option>
                    <option value="Beat - Engineered Hardwood">Beat - Engineered Hardwood</option>
                    <option value="Pulse - Engineered Hardwood">Pulse - Engineered Hardwood</option>
                    <option value="Not Sure">Not Sure - Need Consultation</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="squareFootage" className="block text-gray-700 font-medium mb-2">
                    Approximate Square Footage *
                  </label>
                  <input
                    type="text"
                    id="squareFootage"
                    name="squareFootage"
                    value={formData.squareFootage}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 500 sq ft"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="projectType" className="block text-gray-700 font-medium mb-2">
                    Project Type *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="">Select project type</option>
                    <option value="Residential - New Installation">Residential - New Installation</option>
                    <option value="Residential - Replacement">Residential - Replacement</option>
                    <option value="Commercial - New Installation">Commercial - New Installation</option>
                    <option value="Commercial - Replacement">Commercial - Replacement</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="timeline" className="block text-gray-700 font-medium mb-2">
                    Project Timeline *
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="">Select timeline</option>
                    <option value="ASAP - Within 2 weeks">ASAP - Within 2 weeks</option>
                    <option value="1-2 months">1-2 months</option>
                    <option value="2-3 months">2-3 months</option>
                    <option value="3+ months">3+ months</option>
                    <option value="Just Planning">Just Planning</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="mb-8">
              <label htmlFor="additionalDetails" className="block text-gray-700 font-medium mb-2">
                Additional Details or Special Requirements
              </label>
              <textarea
                id="additionalDetails"
                name="additionalDetails"
                value={formData.additionalDetails}
                onChange={handleChange}
                rows="5"
                placeholder="Tell us more about your project, specific requirements, or any questions you have..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-4 rounded-lg font-semibold text-lg transition-all shadow-lg ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-xl transform hover:-translate-y-0.5"
              }`}
            >
              {isSubmitting ? "Submitting..." : "Request Quote"}
            </button>

            <p className="text-gray-500 text-sm text-center mt-4">
              By submitting this form, you agree to be contacted by Timberlake Flooring regarding your quote request.
            </p>
          </form>
        </div>

        {/* Contact Info */}
        <div className="mt-12 bg-blue-50 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            Prefer to Talk to Us Directly?
          </h3>
          <p className="text-gray-600 mb-4">
            Call us or visit our showroom for immediate assistance
          </p>
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8">
            <a
              href="tel:604-616-3986"
              className="text-blue-600 font-semibold hover:text-blue-700 transition-colors text-lg"
            >
              📞 604-616-3986
            </a>
            <span className="text-gray-400 hidden md:inline">|</span>
            <p className="text-gray-700">
              📍 13331 Vulcan Wy #9, Richmond, BC
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RequestQuote;
